import { useEffect, useState } from "react";
import { sortByName } from "@/lib/listSort";
import { Link } from "react-router-dom";
import { Shield, ArrowRight, Search, Sparkles, Settings2, Package, Play, Square, AlertOctagon, Wrench, DollarSign } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BespokeEditorModal from "@/pages/components/admin/BespokeEditorModal";
import ManageFestivalModal from "@/pages/components/admin/ManageFestivalModal";
import OrganiserPricingModal from "@/pages/components/admin/OrganiserPricingModal";
import OwnerLiveOpsEnterModal from "@/pages/components/admin/OwnerLiveOpsEnterModal";
import EarlyAccessRequestsPanel from "@/pages/components/admin/EarlyAccessRequestsPanel";
import CloneControls from "@/pages/components/admin/CloneControls";
import { markAdminFestivalEntry } from "@/lib/adminNav";

/**
 * Iter324 · Phase C — Owner Admin Console.
 * Owner-only /admin/festivals — lists every organiser's festivals with
 * quick links into per-festival controls (bespoke package, archive
 * window, etc.). Also exposes the global pricing-settings singleton
 * (early_access_hours + archive_window_days).
 */
export default function AdminFestivals() {
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [festSort, setFestSort] = useState("default");
  const [saving, setSaving] = useState(false);
  // Iter325 — Bespoke editor + force-status confirmation.
  const [bespokeTarget, setBespokeTarget] = useState(null);
  const [forceTarget, setForceTarget] = useState(null); // {festival, mode: 'complete'|'active'}
  const [forceBusy, setForceBusy] = useState(false);
  // Iter387 — Festival Manager: Manage modal + Organiser pricing modal.
  const [manageTarget, setManageTarget] = useState(null);
  const [orgPricingTarget, setOrgPricingTarget] = useState(null);
  // Iter388 · Fix 2 — Client-side pagination so pages with 2000+
  // festivals don't render every card upfront. Owner still sees
  // everything via search + Load more.
  const [visibleCount, setVisibleCount] = useState(100);
  // Iter389 · Feature 1 — Owner Live Ops entry modal target.
  const [liveOpsTarget, setLiveOpsTarget] = useState(null);

  const reload = async () => {
    try {
      const fs = await api.get("/admin/festivals");
      setItems(fs.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reload");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [fs, ss] = await Promise.all([
          api.get("/admin/festivals"),
          api.get("/festival/pricing/settings"),
        ]);
        setItems(fs.data.items || []);
        setSettings(ss.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load admin console");
        setItems([]);
      }
    })();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Iter389 · Fix — Explicit coercion so a valid `0` isn't silently
      // rewritten to the default via `|| 25`; the backend then correctly
      // 422s. `formatApiError` collapses FastAPI validation arrays into
      // a readable string so `toast.error` never receives a raw object.
      const ea = settings.early_access_hours === "" || settings.early_access_hours == null
        ? 5 : Number(settings.early_access_hours);
      const aw = settings.archive_window_days === "" || settings.archive_window_days == null
        ? 30 : Number(settings.archive_window_days);
      const r = await api.put("/festival/pricing/settings", {
        early_access_hours: ea,
        archive_window_days: aw,
      });
      setSettings(r.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const filtered = sortByName((items || []).filter((f) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (f.name || "").toLowerCase().includes(needle)
      || (f.tenant?.name || "").toLowerCase().includes(needle)
      || (f.tenant?.email || "").toLowerCase().includes(needle)
    );
  }), (f) => f.name, festSort);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visible.length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-festivals-page">
      <div className="mb-6">
        <div className="label-mono text-[#66C2FF] mb-1">RALLY-LAB · OWNER CONSOLE</div>
        <h1 className="h-display text-3xl sm:text-4xl flex items-center gap-2">
          <Shield className="w-7 h-7 text-[#66C2FF]" /> Every festival
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Platform-wide view. Open a festival to configure a bespoke package, override the early-access window, gift helper accounts, or adjust the archive window.
        </p>
      </div>

      <EarlyAccessRequestsPanel />

      {/* Global settings singleton */}
      <div className="card-surface p-5 mb-6" data-testid="admin-global-settings">
        <div className="label-mono text-[#CCFF00] mb-3">GLOBAL DEFAULTS</div>
        <div className="text-[10px] text-[#A3A3A3] mb-3">
          Default early access hours (1-72) — the number of hours before midnight on festival day 1 that organisers get access to their festival for setup and admin.
        </div>
        {settings ? (
          <div className="grid sm:grid-cols-3 gap-3 items-end">
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Default early access hours (1-72)</div>
              <input
                type="number" min={1} max={72}
                value={settings.early_access_hours ?? ""}
                onChange={(e) => setSettings({ ...settings, early_access_hours: e.target.value })}
                data-testid="admin-early-access-hours"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white mt-1 focus:border-[#CCFF00] focus:outline-none"
              />
              <div className="text-[10px] text-[#525252] mt-1">Hours before 00:01 on festival day 1 that organisers get setup access. Default 5.</div>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Archive window (days)</div>
              <input
                type="number" min={1} max={365}
                value={settings.archive_window_days ?? ""}
                onChange={(e) => setSettings({ ...settings, archive_window_days: e.target.value })}
                data-testid="admin-archive-window-days"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-white mt-1 focus:border-[#CCFF00] focus:outline-none"
              />
              <div className="text-[10px] text-[#525252] mt-1">Auto-archive completed festivals N days after end.</div>
            </label>
            <button
              type="button"
              onClick={saveSettings}
              disabled={saving}
              data-testid="admin-settings-save"
              className="px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white disabled:opacity-40"
            >
              <Settings2 className="w-4 h-4 inline mr-1" /> {saving ? "Saving…" : "Save defaults"}
            </button>
          </div>
        ) : <div className="text-[#A3A3A3] text-sm">Loading…</div>}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Search className="w-4 h-4 text-[#525252]" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by festival, organiser name or email…"
          data-testid="admin-festival-search"
          className="w-full max-w-md bg-[#141414] border border-[#262626] px-3 py-2 text-white text-sm focus:border-[#66C2FF] focus:outline-none"
        />
        <div className="inline-flex border border-[#262626]" role="radiogroup" aria-label="Sort" data-testid="admin-festival-sort">
          {[["default", "Newest"], ["az", "A–Z"], ["za", "Z–A"]].map(([m, l]) => (
            <button key={m} type="button" role="radio" aria-checked={festSort === m} onClick={() => setFestSort(m)} data-testid={`admin-festival-sort-${m}`}
              className={`px-2.5 py-2 text-[10px] uppercase tracking-wider ${festSort === m ? "bg-[#66C2FF] text-black font-bold" : "text-[#A3A3A3] hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {items === null && <div className="text-sm text-[#A3A3A3]">Loading…</div>}
      {items && filtered.length === 0 && (
        <div className="card-surface p-8 text-center text-sm text-[#A3A3A3]" data-testid="admin-festivals-empty">
          <Sparkles className="w-10 h-10 text-[#66C2FF] mx-auto mb-3" />
          No festivals match your search.
        </div>
      )}
      {items && filtered.length > 0 && (
        <>
        <div className="text-[10px] uppercase tracking-widest text-[#525252] mb-2" data-testid="admin-festival-count">
          Showing {visible.length} of {filtered.length}
        </div>
        <div className="grid gap-3 sm:grid-cols-2" data-testid="admin-festival-list">
          {visible.map((f) => (
            <div
              key={f.id}
              data-testid={`admin-festival-card-${f.id}`}
              className="card-surface p-4 hover:border-[#66C2FF] transition-colors block relative"
            >
              <Link to={`/festival/${f.id}`} onClick={markAdminFestivalEntry} className="block">
                <div className="flex items-start justify-between gap-3 mb-1 pr-8">
                  <div className="min-w-0">
                    <div className="font-semibold text-white truncate">{f.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#525252] mt-0.5">
                      {f.tenant?.name || f.tenant?.email || "Unknown organiser"}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <div className="flex items-center gap-1">
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
                        f.derived_status === "live" ? "text-[#CCFF00] border-[#CCFF00]" :
                        f.derived_status === "ended" ? "text-[#525252] border-[#262626]" :
                        "text-[#66C2FF] border-[#66C2FF]"
                      }`} data-testid={`admin-festival-derived-status-${f.id}`}>
                        {f.derived_status || "upcoming"}
                      </span>
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
                        f.license_state === "active_paid" ? "text-[#CCFF00] border-[#CCFF00]" :
                        "text-[#A3A3A3] border-[#262626]"
                      }`}>
                        {f.license_state || "draft"}
                      </span>
                    </div>
                    {/* Iter464 — Tier label beneath the status badges.
                        Mirrors the organiser-view treatment in
                        FestivalHome.jsx so every role sees the same
                        tier on each card. Hidden entirely when no
                        pass has been purchased yet. */}
                    {(() => {
                      const pt = f.pass_type;
                      if (!pt) return null;
                      const map = { starter: "Starter", standard: "Standard", pro: "Pro", unlimited: "Unlimited" };
                      const key = String(pt).split("_")[0];
                      const tierLabel = map[key];
                      if (!tierLabel) return null;
                      return (
                        <span
                          data-testid={`admin-festival-tier-${f.id}`}
                          className="text-[9px] uppercase tracking-widest text-[#A3A3A3]"
                        >
                          {tierLabel}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="text-xs text-[#A3A3A3] flex items-center gap-3 flex-wrap" data-testid={`admin-festival-meta-${f.id}`}>
                  <span>{f.player_count || 0} players</span>
                  <span>· {f.pack_purchased || "-"}</span>
                  {f.start_at && (
                    <span data-testid={`admin-festival-dates-${f.id}`}>
                      · {f.start_at.slice(0, 10)}
                      {f.end_at && f.end_at.slice(0, 10) !== f.start_at.slice(0, 10) ? ` → ${f.end_at.slice(0, 10)}` : ""}
                    </span>
                  )}
                  {f.has_bespoke && (
                    <span className="text-[#FFB020] inline-flex items-center gap-1" data-testid={`admin-festival-bespoke-${f.id}`}>
                      <Package className="w-3 h-3" /> {f.bespoke_name}
                    </span>
                  )}
                </div>
              </Link>
              {/* Iter325/387 — Owner quick actions. */}
              <div className="mt-3 flex items-center gap-1 flex-wrap">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLiveOpsTarget(f); }}
                  data-testid={`admin-enter-live-ops-${f.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black"
                  title="Enter Live Ops as Owner (organiser + deputy notified)"
                >
                  <Play className="w-3 h-3" /> Live Ops
                </button>
                {!f.is_clone && <CloneControls festival={f} onChanged={reload} />}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setManageTarget(f); }}
                  data-testid={`admin-manage-${f.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
                  title="Manage helpers + audit trail"
                >
                  <Wrench className="w-3 h-3" /> Manage
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setOrgPricingTarget(f.tenant); }}
                  data-testid={`admin-org-pricing-${f.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black"
                  title="Set organiser-wide bespoke pricing"
                >
                  <DollarSign className="w-3 h-3" /> Organiser pricing
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setBespokeTarget(f); }}
                  data-testid={`admin-bespoke-open-${f.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black"
                  title={f.has_bespoke ? "Edit bespoke package" : "Add bespoke package"}
                >
                  <Package className="w-3 h-3" /> {f.has_bespoke ? "Edit bespoke" : "Add bespoke"}
                </button>
                {!f.archived_at && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setForceTarget({ festival: f, mode: "complete" }); }}
                    data-testid={`admin-force-complete-${f.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:border-[#FF4500] hover:text-[#FF4500]"
                    title="Force this festival complete"
                  >
                    <Square className="w-3 h-3" /> Force complete
                  </button>
                )}
                {f.archived_at && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setForceTarget({ festival: f, mode: "active" }); }}
                    data-testid={`admin-force-active-${f.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00]"
                    title="Re-open this festival"
                  >
                    <Play className="w-3 h-3" /> Force active
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {hasMore && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => setVisibleCount((c) => c + 100)}
              data-testid="admin-festival-load-more"
              className="px-4 py-2 text-xs uppercase tracking-widest font-bold border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black"
            >
              Load more
            </button>
          </div>
        )}
        </>
      )}

      {/* Iter325 — Bespoke editor modal. */}
      <BespokeEditorModal
        festival={bespokeTarget}
        open={!!bespokeTarget}
        onClose={() => setBespokeTarget(null)}
        onSaved={reload}
      />
      {/* Iter389 · Feature 1 — Owner Live Ops entry confirmation. */}
      <OwnerLiveOpsEnterModal
        festival={liveOpsTarget}
        open={!!liveOpsTarget}
        onClose={() => setLiveOpsTarget(null)}
      />
      {/* Iter387 — Festival Manager modal (Helpers + Audit). */}
      <ManageFestivalModal
        festival={manageTarget}
        open={!!manageTarget}
        onClose={() => setManageTarget(null)}
        onSaved={reload}
      />
      {/* Iter387 — Organiser-wide pricing modal. */}
      <OrganiserPricingModal
        organiser={orgPricingTarget}
        open={!!orgPricingTarget}
        onClose={() => setOrgPricingTarget(null)}
        onSaved={reload}
      />

      {/* Iter325 — Force-status confirmation modal. */}
      {forceTarget && (
        <div
          className="fixed inset-0 z-[95] bg-black/70 flex items-center justify-center p-4"
          onClick={() => !forceBusy && setForceTarget(null)}
          data-testid="admin-force-modal"
        >
          <div
            className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FFB020] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertOctagon className="w-6 h-6 text-[#FFB020] shrink-0 mt-0.5" />
              <div>
                <div className="label-mono text-[#FFB020] text-[10px]">
                  FORCE {forceTarget.mode.toUpperCase()}
                </div>
                <h2 className="h-display text-xl mt-1">Confirm status change</h2>
              </div>
            </div>
            <p className="text-sm text-white mb-4">
              <span className="font-bold">{forceTarget.festival.name}</span> will be
              force-marked <span className="text-[#FFB020]">{forceTarget.mode}</span>.
              {forceTarget.mode === "complete"
                ? " The festival will move to the archived section immediately and Live Ops will close."
                : " The archive stamp will be cleared and Live Ops will re-open subject to the usual gates."}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setForceTarget(null)}
                disabled={forceBusy}
                data-testid="admin-force-cancel"
                className="px-4 py-2 text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={forceBusy}
                onClick={async () => {
                  setForceBusy(true);
                  try {
                    await api.post(`/admin/festival/${forceTarget.festival.id}/force-status`,
                                   { status: forceTarget.mode });
                    toast.success(`Marked ${forceTarget.mode}`);
                    setForceTarget(null);
                    await reload();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Force-status failed");
                  } finally { setForceBusy(false); }
                }}
                data-testid="admin-force-confirm"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#FFB020] text-black hover:bg-white disabled:opacity-40"
              >
                {forceBusy ? "Applying…" : `Force ${forceTarget.mode}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
